import React, { useEffect, useState } from 'react';
import { Checkbox, colors, TableCell, TableRow, Tooltip } from '@insurancemenu/imenu-components-lib';
import { useLazyQuery } from '@apollo/client';
import { GET_ALL_RENEWAL_ENROLLMENT_PERIODS_BY_GROUP_ID } from 'api/queries/enrollmentService';
import { EnrollmentStage, InstalledEnrollmentType } from 'types/enrollmentService';
import { useRootStore } from 'store/rootStore';
import InCellDivisionBlock from 'components/common/group/content/InCellDivisionBlock';
import { EmailMapping, Status } from 'types/emailService';
import {
  RadioButtonUnchecked,
  CheckCircle,
  InfoButton,
  Cart,
  InfoButtonFilled,
  RadioButtonChecked,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { BiGroup } from 'types/groupService';
import { DEFAULT_RENEWAL_ENROLLMENT_OBJECT } from 'store/domain/enrollmentStore';
import { UserType } from 'types/general';
import { observer } from 'mobx-react-lite';
import { getProductLinesFromArray } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { toJS } from 'mobx';
import clsx from 'clsx';
import { dateFormatters, capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import StageTag from 'components/common/enrollment/StageTag';
import { EmailsWarningStatistic } from 'store/ui-stores/trigger-renewals/types';
import { createStyles } from './styles';
import MoreDropdown from './MoreDropdown';
import DynamicCell from './DynamicCell';

interface TriggerRenewalsRowProps {
  group: BiGroup;
}

export type EmailByUserType<T extends string> = Readonly<{
  [key in T]: string[];
}>;

export const EMAIL_STATUS_ICON = Object.freeze({
  SENT: <RadioButtonUnchecked width="2.3rem" height="2.3rem" color={colors.yellow[600]} />,
  OPENED: <CheckCircle width="2.3rem" height="2.3rem" color={colors.green[600]} />,
  NEW: <InfoButton width="2.3rem" height="2.3rem" color={colors.blue[600]} />,
  // Failed Statuses
  BOUNCED: <RadioButtonChecked width="2.3rem" height="2.3rem" color={colors.red[600]} />,
  FAILED_TO_SEND: <RadioButtonChecked width="2.3rem" height="2.3rem" color={colors.orange[600]} />,
  BLOCK: <InfoButtonFilled width="2.3rem" height="2.3rem" color={colors.red[600]} />,
  INVALID: <RadioButtonChecked width="2.3rem" height="2.3rem" color={colors.red[600]} />,
  SPAM: <RadioButtonChecked width="2.3rem" height="2.3rem" color={colors.red[600]} />,
});

const TriggerRenewalsRow = observer(({ group }: TriggerRenewalsRowProps): JSX.Element => {
  const [emails, setEmails] = useState<{ [key: string]: EmailMapping[] }>({});
  const {
    id: groupId,
    name,
    agency,
    contactInfo: { primaryEmail },
    agentOfRecord: { email: aorEmail },
    additionalAORs,
    additionalContactInfos,
  } = group;

  const additionalAORsEmails = additionalAORs.map(({ email }) => email).filter((email) => email);
  const additionalContactInfosEmails = additionalContactInfos
    .filter((admin) => admin.primaryEmail)
    .map((admin) => admin.primaryEmail as string);

  const aorEmails = aorEmail ? [aorEmail, ...additionalAORsEmails] : additionalAORsEmails;
  const adminsEmails = primaryEmail ? [primaryEmail, ...additionalContactInfosEmails] : additionalContactInfosEmails;
  const availableForEmailsStages: EnrollmentStage[] = [EnrollmentStage.RENEWAL, EnrollmentStage.INSTALLED];

  const {
    triggerRenewalsStore: {
      shouldPeriodBeUpdated,
      currentPeriod,
      currentGroup,
      selectedPeriods,
      selectedGroups,
      isSelectAllButtonChecked,
      getEmailMappingsByPeriodId,
      openEmailsModal,
      getEmailsWithStatusNew,
      setShouldPeriodBeUpdated,
      missingExhibitTexts,
      missingProductsOrRatesTexts,
      getMissingProductLines,
      updateSelectedPeriodsList,
      getPeriodSelectedStatus,
      updateAllPeriodsList,
      setMissingUsersEmailsStatistic,
    },
  } = useRootStore();

  const convertedPeriod = toJS(currentPeriod);
  const convertedGroup = toJS(currentGroup);
  const convertedSelectedPeriods = selectedPeriods.map((periodId) => toJS(periodId)).slice();
  const convertedSelectedGroups = selectedGroups.map((selectedGroup) => toJS(selectedGroup)).slice();

  const isGroupSelected = convertedSelectedGroups.find((selectedGroup) => selectedGroup.id === groupId);

  const [
    getAllRenewalEnrollmentPeriodsByGroupId,
    {
      data = {
        getAllRenewalEnrollmentPeriodsByGroupId: [DEFAULT_RENEWAL_ENROLLMENT_OBJECT],
      },
    },
  ] = useLazyQuery<{
    getAllRenewalEnrollmentPeriodsByGroupId: InstalledEnrollmentType[];
  }>(GET_ALL_RENEWAL_ENROLLMENT_PERIODS_BY_GROUP_ID, {
    fetchPolicy: 'no-cache',
    variables: { groupId: group.id, showInstalled: true },
  });

  useEffect(() => {
    void getAllRenewalEnrollmentPeriodsByGroupId();
  }, [getAllRenewalEnrollmentPeriodsByGroupId]);

  const getEmailsByPeriodId = async (periodId: string): Promise<void> => {
    const emailsWithStatuses = await getEmailMappingsByPeriodId(periodId); // emails with statuses from EmailService
    const newAdminEmails = getEmailsWithStatusNew(UserType.ADMIN, emailsWithStatuses, periodId, adminsEmails);
    const newAorsEmails = getEmailsWithStatusNew(UserType.AOR, emailsWithStatuses, periodId, aorEmails);
    const allEmails = [...newAdminEmails, ...newAorsEmails, ...emailsWithStatuses];

    setEmails((prevEmails) => ({
      ...prevEmails,
      [periodId]: allEmails,
    }));
  };

  // This function works incorrectly: the promises are not awaited, rewrite it with Promise.all or remove await whatsoever.
  // eslint-disable-next-line @typescript-eslint/require-await
  const getEmails = async (periods: InstalledEnrollmentType[]): Promise<void> => {
    periods.forEach(async (period) => {
      if (period.id) {
        await getEmailsByPeriodId(period.id);
      }
    });
  };

  const getEmailsWarningStatistic = (): EmailsWarningStatistic => {
    const missingAdditionalAORs = additionalAORs.map(({ email }) => email).filter((email) => !email);
    const missingAORsCount = aorEmail ? missingAdditionalAORs.length : missingAdditionalAORs.length + 1;

    const missingAdditionalAdmins = additionalContactInfos
      .filter((admin) => !admin.primaryEmail)
      .map((admin) => admin.primaryEmail);
    const missingAdminsCount = primaryEmail ? missingAdditionalAdmins.length : missingAdditionalAdmins.length + 1;

    return { [UserType.AOR]: missingAORsCount, [UserType.ADMIN]: missingAdminsCount };
  };

  useEffect(() => {
    if (data.getAllRenewalEnrollmentPeriodsByGroupId.length > 0) {
      const filteredPeriods = [...data.getAllRenewalEnrollmentPeriodsByGroupId].filter((period) => period.id);
      void getEmails(filteredPeriods);
      updateAllPeriodsList(filteredPeriods);
    }
  }, [data.getAllRenewalEnrollmentPeriodsByGroupId]);

  useEffect(() => {
    // Update list of periods if necessary for a single group.
    const filteredPeriods = [...data.getAllRenewalEnrollmentPeriodsByGroupId].filter((period) => period.id);
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    if (
      filteredPeriods.length > 0 &&
      shouldPeriodBeUpdated &&
      (isSelectAllButtonChecked || convertedGroup.id === groupId || isGroupSelected)
    ) {
      timeoutId = setTimeout(async (): Promise<void> => {
        await getAllRenewalEnrollmentPeriodsByGroupId().then(async () => {
          if (Object.keys(emails).includes(convertedPeriod.id)) {
            // Emails send in a queue, so to receive actual list, users should reload page in couple of minutes
            // Updating 1 email status works fine
            await getEmails(filteredPeriods); // Retrieves and displays updated email statuses.
            setShouldPeriodBeUpdated(false);
          }
        }); // Updates the list of periods to display the most recent changes
      }, 2000);
    }
    return (): void => {
      setEmails({});
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldPeriodBeUpdated]);

  const hasSelectedPeriodInGroup = [...data.getAllRenewalEnrollmentPeriodsByGroupId].find((period) => {
    return getPeriodSelectedStatus(period);
  });

  const classes = createStyles(hasSelectedPeriodInGroup);

  const getEmailByUserType = (periods: InstalledEnrollmentType[], type: UserType): JSX.Element[] => {
    const emailCells: JSX.Element[] = [];

    periods
      .filter(({ productLineIds, id }) => productLineIds.length > 0 && id)
      .forEach((period) => {
        const isPeriodSelected = getPeriodSelectedStatus(period);

        const emailsByPeriod = emails[period.id]?.filter((email) => email.userType === type) || [];
        const emailsStatuses = emailsByPeriod.map(({ emailAddress, userType, status }) => {
          return (
            <div className={classes.status} key={`${userType}_${emailAddress}_${period.id}_${Status[status]}`}>
              {availableForEmailsStages.includes(period.stage) && (
                <Tooltip alignment={AlignmentTypes.RIGHT_CENTER} label={capitalize(Status[status].toLowerCase())}>
                  {EMAIL_STATUS_ICON[status]}
                </Tooltip>
              )}
            </div>
          );
        });

        emailCells.push(
          <InCellDivisionBlock
            withBorder
            key={`${type}_emails_${period.id}`}
            className={clsx(classes.customInCellBlock, isPeriodSelected && classes.selectedCellBlock)}
          >
            <div className={classes.statusesList}>{emailsStatuses}</div>
          </InCellDivisionBlock>,
        );
      });

    return emailCells;
  };

  const moreOptionsDropdowns = (periods: InstalledEnrollmentType[]): JSX.Element[] => {
    const buttons: JSX.Element[] = [];
    periods
      .filter(({ productLineIds }) => productLineIds.length > 0)
      .forEach((period) => {
        const isEmailsListEmpty = !(emails[period.id]?.length > 0);
        const isPeriodSelected = getPeriodSelectedStatus(period);

        buttons.push(
          <InCellDivisionBlock
            withBorder
            key={`dropdown_${period.id}`}
            className={clsx(classes.customInCellBlock, isPeriodSelected && classes.selectedCellBlock)}
          >
            <div className={classes.actionBlock}>
              <MoreDropdown
                group={group}
                period={period}
                groupPeriods={data.getAllRenewalEnrollmentPeriodsByGroupId}
                isManageEmailsAvailable={availableForEmailsStages.includes(period.stage) && !isEmailsListEmpty}
                openEmailsModal={(): void => {
                  openEmailsModal(emails[period.id], period, group);
                  setMissingUsersEmailsStatistic(getEmailsWarningStatistic());
                }}
              />
              <div className={classes.checkboxWrapper}>
                <Checkbox
                  label=""
                  checked={isPeriodSelected}
                  highlightColor={colors.blue[600]}
                  onChange={(): void => {
                    updateSelectedPeriodsList(period, group);
                  }}
                />
              </div>
            </div>
          </InCellDivisionBlock>,
        );
      });
    return buttons;
  };

  const productLinesWithTooltip = (period: InstalledEnrollmentType): JSX.Element => {
    const missingProductLines = getMissingProductLines(period);
    const missingProductsOrRates = missingProductsOrRatesTexts(period);

    const currentProductsLines = getProductLinesFromArray(period.productLineIds);
    const haveMismatchedProductsAndExhibits = period.installedProposal?.productLines.some(
      (productLine) => productLine.products.length !== productLine.exhibitUrls.length,
    );

    return (
      <div className={classes.productLineCellContent}>
        {missingProductLines.length > 0 ? (
          <Tooltip alignment={AlignmentTypes.RIGHT_CENTER} label={missingExhibitTexts(period)}>
            <div className={classes.tooltipContent}>
              <span>{currentProductsLines}</span>
              <InfoButton width="2rem" height="2rem" color={colors.red[600]} />
            </div>
          </Tooltip>
        ) : (
          currentProductsLines
        )}

        {missingProductLines.length < 1 && haveMismatchedProductsAndExhibits ? (
          <Tooltip alignment={AlignmentTypes.RIGHT_CENTER} label="There is a mismatch between products & exhibits">
            <div className={classes.tooltipContent}>
              <InfoButton width="2rem" height="2rem" color={colors.yellow[600]} />
            </div>
          </Tooltip>
        ) : null}

        {missingProductsOrRates?.type && (
          <Tooltip alignment={AlignmentTypes.RIGHT_CENTER} label={missingProductsOrRates?.message}>
            <div className={classes.tooltipContent}>
              <Cart
                width="2rem"
                height="2rem"
                color={missingProductsOrRates?.type === 'RATES' ? colors.yellow[600] : colors.red[600]}
              />
            </div>
          </Tooltip>
        )}

        <br />
      </div>
    );
  };

  const getEffectiveDate = (enrollmentPeriod: InstalledEnrollmentType): string => {
    return dateFormatters.normDateToShortAmericanDate(enrollmentPeriod.effectiveDate);
  };

  const getStage = (enrollmentPeriod: InstalledEnrollmentType): JSX.Element => {
    return <StageTag stage={enrollmentPeriod.stage} />;
  };

  // Cells content
  const effectiveDatesCellContent = DynamicCell(
    data.getAllRenewalEnrollmentPeriodsByGroupId,
    convertedSelectedPeriods,
    'effectiveDate',
    getEffectiveDate,
  );

  const productLinesCellContent = DynamicCell(
    data.getAllRenewalEnrollmentPeriodsByGroupId,
    convertedSelectedPeriods,
    'productLines',
    productLinesWithTooltip,
  );

  const stagesCellContent = DynamicCell(
    data.getAllRenewalEnrollmentPeriodsByGroupId,
    convertedSelectedPeriods,
    'stage',
    getStage,
  );

  const aorEmailsCellContent = getEmailByUserType(data.getAllRenewalEnrollmentPeriodsByGroupId, UserType.AOR);
  const adminEmailsCellContent = getEmailByUserType(data.getAllRenewalEnrollmentPeriodsByGroupId, UserType.ADMIN);
  const moreOptionsCellContent = moreOptionsDropdowns(data.getAllRenewalEnrollmentPeriodsByGroupId);

  return (
    <TableRow className={clsx(classes.tableRow, hasSelectedPeriodInGroup && classes.selectedRow)} key={groupId}>
      <TableCell className={classes.tableCell}>{agency.name}</TableCell>
      <TableCell className={classes.tableCell}>{name}</TableCell>
      <TableCell className={classes.tableCell}>{effectiveDatesCellContent}</TableCell>
      <TableCell className={classes.tableCell}>{productLinesCellContent}</TableCell>
      <TableCell className={classes.tableCell}>{stagesCellContent}</TableCell>
      <TableCell className={classes.tableCell}>{aorEmailsCellContent}</TableCell>
      <TableCell className={classes.tableCell}>{adminEmailsCellContent}</TableCell>
      <TableCell className={classes.tableCell}>{moreOptionsCellContent}</TableCell>
    </TableRow>
  );
});

export default TriggerRenewalsRow;
